const JPEG_QUALITY = 0.85;
// iOS Safari refuses to draw into canvases larger than 16,777,216 pixels.
const MAX_CANVAS_PIXELS = 16_777_216;
const PASSTHROUGH_MIME_TYPES: ReadonlySet<string> = new Set(["image/gif", "image/svg+xml"]);

function jpegFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem || "image"}.jpg`;
}

function encodeJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
}

/**
 * Re-encodes a raster image as JPEG at quality 85 on a white background,
 * applying EXIF orientation. Returns the original file when the browser cannot
 * decode or encode it, or when the JPEG is not smaller.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  if (PASSTHROUGH_MIME_TYPES.has(file.type) || typeof createImageBitmap !== "function") {
    return file;
  }
  let bitmap: ImageBitmap | undefined;
  const canvas = document.createElement("canvas");
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (bitmap.width * bitmap.height)));
    canvas.width = Math.max(1, Math.floor(bitmap.width * scale));
    canvas.height = Math.max(1, Math.floor(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await encodeJpeg(canvas);
    if (!blob || blob.type !== "image/jpeg" || blob.size >= file.size) return file;
    return new File([blob], jpegFileName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
    canvas.width = 0;
    canvas.height = 0;
  }
}
